import { useEffect, useState } from 'react';
import type { CartLine } from '../lib/cart';
import { formatPrice } from '../lib/cart';
import {
  availableModes,
  checkoutTotalCents,
  serviceFeeFor,
  deliveryFeeFor,
  validateCheckout,
  isValidCustomerName,
  isValidEmail,
  isValidMobileFRCH,
  type DeliveryConfig,
  type PickupMode,
  type CheckoutState,
} from '../lib/checkout';
import { subtotalCents } from '../lib/cart';
import type { TimeSlot } from '../lib/slots';
import { effectiveDelivery, detectDeliveryZone, hasNpaZones, type DeliveryZone } from '../lib/deliveryZones';
import { getLoyaltyState, stampProgress, isPlausibleEmail, type LoyaltyState } from '../lib/loyaltyClient';
import { previewPromoCode, type PromoPreview } from '../lib/promoService';
import { promoRejectionMessage, normalizePromoCode, type PromoRejectReason } from '../lib/promoCodes';

/**
 * Checkout (US-505) : sélecteur retrait/livraison (critère #11), frais & minimum (critère #12),
 * créneau, paiement, puis validation via la logique migrée (checkout.ts).
 *
 * Présentation PARITÉ LEGACY (2026-06-24) : page « Finaliser la commande » en 2 colonnes —
 * formulaire à gauche, **Récapitulatif collant** à droite (sous-total / frais / total / CTA).
 */
interface CheckoutProps {
  lines: CartLine[];
  delivery: DeliveryConfig;
  currency?: 'EUR' | 'CHF';
  onConfirm: (state: CheckoutState) => void;
  /** Commande en cours d'envoi (désactive le bouton). */
  busy?: boolean;
  /** Message d'erreur de commande/paiement à afficher. */
  error?: string | null;
  /** Zones de livraison (parité C5) — si présentes, le client choisit sa zone. */
  zones?: DeliveryZone[];
  /**
   * Paiement en ligne réellement actif (Stripe onboardé côté resto). Si false,
   * « Carte bancaire en ligne » s'affiche en « Bientôt disponible » et le client
   * est basculé sur « Payer sur place » (parité legacy fix #52).
   */
  stripeActive?: boolean;
  /** Créneau choisi à l'étape précédente (page « Heure de retrait » dédiée). */
  initialSlot?: string | null;
  /** Objet créneau choisi (pour l'affichage du récap « Modifier »). */
  initialPickedSlot?: TimeSlot | null;
  /** Retour à la page créneau dédiée (bouton « Modifier »). */
  onEditSlot?: () => void;
  /** Remarque saisie dans le panier (parité legacy) — pré-remplit la commande. */
  initialNote?: string;
  /** Fidélité active pour ce resto → affiche la carte de fidélité au checkout. */
  loyaltyActive?: boolean;
  /** Id du tenant (pour lire l'état fidélité du client par email). */
  tenantId?: string;
  /** Seuil de récompense (nb de commandes) et libellé — repli 10 / générique. */
  loyaltyThreshold?: number | null;
  loyaltyReward?: string | null;
  /** Nom du restaurant (libellé du consentement marketing RGPD). */
  restaurantName?: string;
}

export default function Checkout({
  lines,
  delivery,
  currency = 'EUR',
  onConfirm,
  busy = false,
  error = null,
  zones = [],
  stripeActive = false,
  initialSlot = null,
  initialPickedSlot = null,
  onEditSlot,
  initialNote = '',
  loyaltyActive = false,
  tenantId,
  loyaltyThreshold = null,
  loyaltyReward = null,
  restaurantName,
}: CheckoutProps) {
  const modes = availableModes(delivery);
  const [state, setState] = useState<CheckoutState>({
    mode: 'pickup',
    slot: initialSlot,
    paymentMethod: null,
    note: initialNote,
    // Suivi de commande (WhatsApp/SMS/email) : accordé en passant commande, via la mention
    // affichée sous le bouton. Pas de case à cocher (exécution du contrat), mais la valeur
    // reste explicite et révocable — les cases MARKETING, elles, restent décochées.
    whatsappConsent: true,
  });
  const [zoneName, setZoneName] = useState<string | null>(null);
  // Mode « détection par NPA » (parité legacy v11) si les zones ont des localités ;
  // sinon mode « sélection dans une liste » (zones simples).
  const npaMode = hasNpaZones(zones);
  const detectedZone = npaMode
    ? detectDeliveryZone(state.deliveryPostal ?? null, state.deliveryCity ?? null, zones)
    : null;
  const addressFilled = Boolean((state.deliveryStreet ?? '').trim() && (state.deliveryPostal ?? '').trim() && (state.deliveryCity ?? '').trim());
  const outOfZone = npaMode && state.mode === 'delivery' && addressFilled && !detectedZone;
  // Frais/minimum effectifs : zone détectée (NPA) ou zone choisie (liste) ou config simple.
  const effDelivery: DeliveryConfig = npaMode
    ? (detectedZone
        ? { enabled: true, feeCents: detectedZone.feeCents, minCents: detectedZone.minCents }
        : { enabled: true, feeCents: 0, minCents: Number.MAX_SAFE_INTEGER })
    : effectiveDelivery(delivery, zones, zoneName);
  // §F-20 : le titre dépend de l'activation de la livraison (tenant), pas du mode choisi.
  const slotTitle = delivery.enabled ? 'Heure de retrait ou de livraison' : 'Heure de retrait';
  // Créneau choisi à l'étape précédente (page dédiée). Conservé pour le récap « Modifier ».
  const pickedSlot = initialPickedSlot;

  const total = checkoutTotalCents(lines, state.mode, effDelivery);
  const validation = validateCheckout(lines, state, effDelivery);

  const set = (patch: Partial<CheckoutState>) => setState((s) => ({ ...s, ...patch }));

  // ---- Code promo (aperçu serveur ; la remise est reconfirmée au paiement) ----
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<PromoPreview | null>(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const discountCents = promo?.valid ? promo.discountCents : 0;
  const payable = Math.max(0, total - discountCents);
  // Si le panier/mode change, un aperçu appliqué peut devenir périmé → on le réinitialise.
  useEffect(() => {
    if (promo) setPromo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);
  const applyPromo = async () => {
    const code = normalizePromoCode(promoInput);
    if (!code || !tenantId) return;
    setPromoBusy(true);
    // ASSIETTE DE LA REMISE = SOUS-TOTAL ARTICLES uniquement (décision Ahmed du 12/07).
    // Les frais de commande et de livraison ne sont JAMAIS remisés : -20 % porte sur ce
    // que le client mange, pas sur le service. (Avant : la remise portait sur le total TTC,
    // frais inclus, ce qui la rendait mécaniquement plus généreuse.)
    const res = await previewPromoCode(tenantId, code, subtotalCents(lines), state.phone ?? '');
    setPromo(res);
    setPromoBusy(false);
  };

  // Style champ : neutre / vert si valide / rouge si saisi mais invalide.
  const fieldClass = (valid: boolean, val?: string) =>
    'w-full rounded-lg border p-2 text-sm focus:outline-none focus:ring-2 ' +
    (val && !valid ? 'border-red-400 bg-red-50' : val && valid ? 'border-emerald-400' : 'border-slate-200');

  // Stripe pas actif mais « en ligne » sélectionné → on rebascule sur « sur place »
  // (le resto n'a pas encore terminé son onboarding paiement). Parité legacy fix #52.
  useEffect(() => {
    if (!stripeActive && state.paymentMethod === 'online') set({ paymentMethod: 'onsite' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeActive, state.paymentMethod]);

  const deliveryFee = deliveryFeeFor(state.mode, effDelivery);
  const serviceFee = serviceFeeFor(lines);
  const cardClass = 'rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5';

  // CTA défini UNE fois → affiché en haut ET dans le récapitulatif (aucune divergence de logique).
  const confirmLabel = busy
    ? 'Traitement…'
    : state.paymentMethod === 'online'
      ? `Payer ${formatPrice(payable, currency)}`
      : 'Confirmer la commande';
  const handleConfirm = () =>
    onConfirm({
      ...state,
      deliveryFeeCents: effDelivery.feeCents,
      deliveryMinCents: effDelivery.minCents,
      promoCode: promo?.valid ? normalizePromoCode(promoInput) : null,
      promoDiscountCents: discountCents,
    });
  const renderConfirm = (extra = '') => (
    <button
      disabled={!validation.ok || busy}
      onClick={handleConfirm}
      className={`w-full rounded-xl py-3.5 text-base font-bold text-white shadow-lg transition active:scale-95 disabled:opacity-40 ${extra}`}
      style={{ background: 'var(--brand-primary)' }}
    >
      {confirmLabel}
    </button>
  );

  /**
   * CGV — acceptation OBLIGATOIRE : tant que la case n'est pas cochée, `validateCheckout`
   * refuse la commande et le bouton reste désactivé. Décochée par défaut : une case
   * pré-cochée ne vaut pas acceptation.
   *
   * ⚠️ On n'y glisse SURTOUT PAS les consentements marketing. Conditionner l'achat à une
   * acceptation de prospection rendrait ce consentement invalide (RGPD : « librement
   * donné »). Les cases marketing restent séparées, décochées, et facultatives.
   *
   * La MENTION en dessous porte l'accord pour le suivi de commande (WhatsApp/SMS/email) :
   * pas de case, car informer le client de l'état de SA commande relève de l'exécution du
   * contrat, et Meta accepte depuis nov. 2024 un opt-in général nommant l'entreprise.
   */
  const termsBlock = (
    <>
      <label className="mt-4 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={state.termsAccepted === true}
          onChange={(e) => set({ termsAccepted: e.target.checked })}
          className="mt-0.5 h-4 w-4 flex-shrink-0"
        />
        <span>
          J'ai lu et j'accepte les{' '}
          <a href="/cgv" target="_blank" rel="noopener noreferrer" className="font-semibold underline">
            conditions générales de vente
          </a>
          . <span className="text-red-600">*</span>
        </span>
      </label>
      <p className="mt-2 text-center text-xs text-slate-400">
        En passant commande, vous acceptez de recevoir le suivi de votre commande par WhatsApp, SMS ou email
        {restaurantName ? ` de la part de ${restaurantName}` : ''}.
      </p>
    </>
  );

  return (
    <section className="w-full">
      <h1 className="display-font mb-5 text-3xl font-black sm:text-4xl">Finaliser la commande</h1>

      {/* CTA aussi EN HAUT (accessible sans scroller) — le même bouton qu'en bas. */}
      <div className="mb-5">
        {renderConfirm()}
        {termsBlock}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* ===================== COLONNE GAUCHE — formulaire ===================== */}
        <div className="flex flex-col gap-4">
          {/* Mode retrait / livraison (cartes — parité legacy) */}
          {modes.length > 1 && (
            <div className={cardClass}>
              <h3 className="mb-3 text-base font-extrabold">Comment souhaitez-vous récupérer votre commande ?</h3>
              <div className="grid grid-cols-2 gap-3">
                {modes.map((m: PickupMode) => {
                  const on = state.mode === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => set({ mode: m })}
                      className={'rounded-xl border-2 p-4 text-center transition ' + (on ? '' : 'border-slate-200 hover:border-slate-300')}
                      style={on ? { borderColor: 'var(--brand-primary)', background: 'var(--brand-light)' } : {}}
                    >
                      <span className="block text-2xl">{m === 'pickup' ? '🥡' : '🛵'}</span>
                      <span className="mt-1 block text-sm font-bold">{m === 'pickup' ? 'Retrait sur place' : 'Livraison'}</span>
                      <span className="mt-0.5 block text-[11px] text-slate-500">
                        {m === 'pickup'
                          ? 'Gratuit · au comptoir'
                          : `Min. dès ${formatPrice(delivery.minCents, currency)} selon zone`}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Adresse (mode détection NPA) */}
              {state.mode === 'delivery' && npaMode && (
                <div className="mt-4 flex flex-col gap-2">
                  {detectedZone && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                      ✅ {detectedZone.name} · livraison {detectedZone.feeCents === 0 ? 'gratuite' : formatPrice(detectedZone.feeCents, currency)} · min. {formatPrice(detectedZone.minCents, currency)}
                    </div>
                  )}
                  {outOfZone && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      📍 Hors zone de livraison. Vérifiez votre NPA et votre ville, ou choisissez le retrait.
                    </div>
                  )}
                  <input
                    value={state.deliveryStreet ?? ''}
                    onChange={(e) => set({ deliveryStreet: e.target.value })}
                    placeholder="Rue et numéro *"
                    autoComplete="street-address"
                    className="w-full rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2"
                  />
                  <div className="flex gap-2">
                    <input
                      value={state.deliveryPostal ?? ''}
                      onChange={(e) => set({ deliveryPostal: e.target.value })}
                      placeholder="NPA *"
                      inputMode="numeric"
                      className="w-1/3 rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2"
                    />
                    <input
                      value={state.deliveryCity ?? ''}
                      onChange={(e) => set({ deliveryCity: e.target.value })}
                      placeholder="Ville *"
                      className="flex-1 rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2"
                    />
                  </div>
                </div>
              )}

              {/* Adresse (mode sélection de zone simple) */}
              {state.mode === 'delivery' && !npaMode && zones.length > 0 && (
                <div className="mt-4 flex flex-col gap-1">
                  <label htmlFor="delivery-zone" className="text-sm font-semibold">Zone de livraison</label>
                  <select
                    id="delivery-zone"
                    value={zoneName ?? ''}
                    onChange={(e) => setZoneName(e.target.value || null)}
                    className="rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2"
                    style={{ ['--tw-ring-color' as string]: 'var(--brand-primary)' }}
                  >
                    <option value="">Choisissez votre zone…</option>
                    {zones.map((z) => (
                      <option key={z.name} value={z.name}>
                        {z.name} — {formatPrice(z.feeCents, currency)}
                        {z.minCents > 0 ? ` (min. ${formatPrice(z.minCents, currency)})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {state.mode === 'delivery' && !npaMode && (zones.length === 0 || zoneName) && (
                <p className="mt-2 text-sm opacity-70">
                  Frais de livraison : {formatPrice(effDelivery.feeCents, currency)} · minimum {formatPrice(effDelivery.minCents, currency)}
                </p>
              )}
            </div>
          )}

          {/* Créneau (choisi à l'étape précédente — page « Heure de retrait » dédiée) */}
          <div className={cardClass}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-extrabold">{slotTitle}</h3>
                {pickedSlot ? (
                  <p className="mt-1 text-sm font-semibold text-emerald-700">
                    ✅ {pickedSlot.label}
                    {pickedSlot.dayLabel ? ` · ${pickedSlot.dayLabel}` : ''}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-red-600">Aucun créneau sélectionné.</p>
                )}
              </div>
              {onEditSlot && (
                <button
                  type="button"
                  onClick={onEditSlot}
                  className="flex-shrink-0 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                >
                  Modifier
                </button>
              )}
            </div>
          </div>

          {/* Coordonnées client */}
          <div className={cardClass}>
            <h3 className="mb-3 text-base font-extrabold">Vos coordonnées</h3>
            <div className="flex flex-col gap-2">
              <input
                value={state.customerName ?? ''}
                onChange={(e) => set({ customerName: e.target.value })}
                placeholder="Nom et prénom *"
                autoComplete="name"
                className={fieldClass(isValidCustomerName(state.customerName), state.customerName)}
              />
              <input
                type="email"
                value={state.email ?? ''}
                onChange={(e) => set({ email: e.target.value })}
                placeholder="Email (pour le reçu) *"
                autoComplete="email"
                inputMode="email"
                className={fieldClass(isValidEmail(state.email), state.email)}
              />
              <input
                type="tel"
                value={state.phone ?? ''}
                onChange={(e) => set({ phone: e.target.value })}
                placeholder="Mobile * (FR 06… / CH 07…)"
                autoComplete="tel"
                inputMode="tel"
                className={fieldClass(isValidMobileFRCH(state.phone), state.phone)}
              />
              {state.phone && !isValidMobileFRCH(state.phone) && (
                <span className="text-xs text-red-600">📱 Mobile France (06/07) ou Suisse (07x / +41).</span>
              )}
            </div>
          </div>

          {/* Carte de fidélité (parité legacy) — sous les coordonnées, avec l'avancement des ventes. */}
          {loyaltyActive && tenantId && (
            <LoyaltyCheckoutCard
              tenantId={tenantId}
              email={state.email ?? ''}
              threshold={loyaltyThreshold ?? 10}
              reward={loyaltyReward ?? null}
              cardClass={cardClass}
            />
          )}

          {/* Restez en contact (RGPD) — opt-in, cases DÉCOCHÉES par défaut (parité legacy). */}
          <div className={cardClass}>
            <h3 className="text-base font-extrabold">
              Restez en contact <span className="font-normal opacity-50">(facultatif)</span>
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Aucune case n'est pré-cochée. Vous pouvez vous désinscrire à tout moment.
            </p>
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!state.marketingConsent}
                onChange={(e) => set({ marketingConsent: e.target.checked })}
                className="mt-0.5 h-4 w-4 flex-shrink-0"
              />
              <span>Recevoir les offres et nouveautés{restaurantName ? ` de ${restaurantName}` : ' de ce restaurant'}.</span>
            </label>
            <label className="mt-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!state.networkConsent}
                onChange={(e) => set({ networkConsent: e.target.checked })}
                className="mt-0.5 h-4 w-4 flex-shrink-0"
              />
              <span>Recevoir les offres du réseau de restaurants partenaires Lil &amp; Co.</span>
            </label>
          </div>

          {/* Remarque : saisie dans le panier (parité legacy) → reçue ici via initialNote. */}

          {/* Mode de paiement */}
          <div className={cardClass}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-extrabold">Mode de paiement</h3>
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                Sécurisé
              </span>
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                disabled={!stripeActive}
                onClick={() => stripeActive && set({ paymentMethod: 'online' })}
                className={
                  'relative flex w-full items-center justify-between rounded-xl border-2 p-4 text-left shadow-sm transition ' +
                  (!stripeActive
                    ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'
                    : state.paymentMethod === 'online'
                      ? ''
                      : 'border-gray-200 bg-white hover:border-gray-300')
                }
                style={stripeActive && state.paymentMethod === 'online' ? { borderColor: 'var(--brand-primary)', background: 'var(--brand-light)' } : {}}
              >
                {stripeActive ? (
                  <span className="absolute -top-2 left-4 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow" style={{ background: 'var(--brand-primary)' }}>
                    ★ Recommandé
                  </span>
                ) : (
                  <span className="absolute -top-2 left-4 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow">
                    Bientôt disponible
                  </span>
                )}
                <span className="flex items-center gap-3">
                  <span className="text-3xl">💳</span>
                  <span>
                    <span className="block text-base font-extrabold">Carte bancaire en ligne</span>
                    <span className="mt-0.5 block text-xs leading-snug text-gray-600">
                      {stripeActive ? (
                        <>
                          <span className="font-semibold text-emerald-700">✓ Aucune attente</span> à la caisse · Paiement <b>3D Secure Stripe</b>
                        </>
                      ) : (
                        <span className="text-amber-700">Activation Stripe en cours côté restaurant. Choisissez « Sur place » pour cette commande.</span>
                      )}
                    </span>
                  </span>
                </span>
                {stripeActive && state.paymentMethod === 'online' && (
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: 'var(--brand-primary)' }}>✓</span>
                )}
              </button>

              <button
                type="button"
                onClick={() => set({ paymentMethod: 'onsite' })}
                className={'flex w-full items-center justify-between rounded-xl border p-4 text-left transition ' + (state.paymentMethod === 'onsite' ? 'border-2' : 'border-gray-200 opacity-90 hover:border-gray-300')}
                style={state.paymentMethod === 'onsite' ? { borderColor: 'var(--brand-primary)', background: 'var(--brand-light)' } : {}}
              >
                <span className="flex items-center gap-3">
                  <span className="text-xl opacity-70">💵</span>
                  <span>
                    <span className="block text-sm font-bold">Payer sur place</span>
                    <span className="mt-0.5 block text-[11px] text-gray-500">CB ou espèces au comptoir · Confirmation immédiate</span>
                  </span>
                </span>
                {state.paymentMethod === 'onsite' && (
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs text-white" style={{ background: 'var(--brand-primary)' }}>✓</span>
                )}
              </button>
            </div>

            {state.paymentMethod === 'online' && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-gray-50 p-3 text-[11px] leading-snug text-gray-600">
                <span className="text-base">🔒</span>
                <span>Votre carte n'est <b>jamais stockée</b> sur ce site. Paiement traité par <b>Stripe</b> (PCI DSS Level 1).</span>
              </div>
            )}
          </div>
        </div>

        {/* ===================== COLONNE DROITE — Récapitulatif collant ===================== */}
        <aside className="h-fit lg:sticky lg:top-24">
          <div className={cardClass}>
            <h3 className="mb-3 text-lg font-extrabold">Récapitulatif</h3>
            <div className="flex flex-col gap-1.5 text-sm">
              <div className="flex items-center justify-between text-slate-600">
                <span>Sous-total</span>
                <span>{formatPrice(subtotalCents(lines), currency)}</span>
              </div>
              <div className="flex items-center justify-between text-slate-600">
                <span>Frais de commande</span>
                <span>{formatPrice(serviceFee, currency)}</span>
              </div>
              {state.mode === 'delivery' && deliveryFee > 0 && (
                <div className="flex items-center justify-between text-slate-600">
                  <span>Livraison</span>
                  <span>{formatPrice(deliveryFee, currency)}</span>
                </div>
              )}
              {discountCents > 0 && (
                <div className="flex items-center justify-between font-semibold text-emerald-700">
                  <span>Code promo {promo?.valid && promoInput ? `(${normalizePromoCode(promoInput)})` : ''}</span>
                  <span>-{formatPrice(discountCents, currency)}</span>
                </div>
              )}
              <div className="my-2 border-t border-slate-100" />
              <div className="flex items-center justify-between text-lg font-extrabold">
                <span>Total</span>
                <span style={{ color: 'var(--brand-primary)' }}>{formatPrice(payable, currency)}</span>
              </div>
            </div>

            {/* Code promo (SMS de relance) — validé côté serveur, appliqué au paiement en ligne. */}
            <div className="mt-3 rounded-lg border border-slate-200 p-3">
              <label htmlFor="promo" className="mb-1 block text-xs font-semibold text-slate-600">🎟️ Code promo</label>
              <div className="flex gap-2">
                <input
                  id="promo"
                  value={promoInput}
                  onChange={(e) => { setPromoInput(e.target.value); if (promo) setPromo(null); }}
                  placeholder="Votre code"
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 p-2 text-sm uppercase focus:outline-none focus:ring-2"
                />
                <button
                  type="button"
                  onClick={applyPromo}
                  disabled={promoBusy || !promoInput.trim()}
                  className="flex-shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
                  style={{ background: 'var(--brand-primary)' }}
                >
                  {promoBusy ? '…' : 'Appliquer'}
                </button>
              </div>
              {promo && promo.valid && (
                <p className="mt-1.5 text-xs font-semibold text-emerald-700">
                  ✅ Réduction de {formatPrice(promo.discountCents, currency)} appliquée · valable au paiement en ligne.
                </p>
              )}
              {promo && !promo.valid && (
                <p className="mt-1.5 text-xs font-semibold text-red-600">
                  {promoRejectionMessage((promo.reason ?? 'inactive') as PromoRejectReason)}
                </p>
              )}
            </div>

            {!validation.ok && (
              <ul className="mt-3 list-disc pl-5 text-xs text-red-600">
                {validation.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
            {error && <p className="mt-3 text-sm font-semibold text-red-600">{error}</p>}

            {renderConfirm('mt-4')}
            {termsBlock}

            <p className="mt-3 text-center text-[11px] text-slate-400">
              🔒 Paiement sécurisé · Aucun frais caché · 0 % commission au resto
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

/**
 * Carte de fidélité au checkout (parité legacy) — sous les coordonnées.
 * Affiche l'offre (seuil + récompense) et, dès que l'email est renseigné,
 * la PROGRESSION du client (X / seuil ventes avant la récompense).
 */
function LoyaltyCheckoutCard({
  tenantId,
  email,
  threshold,
  reward,
  cardClass,
}: {
  tenantId: string;
  email: string;
  threshold: number;
  reward: string | null;
  cardClass: string;
}) {
  const [state, setState] = useState<LoyaltyState | null>(null);
  useEffect(() => {
    if (!tenantId || !isPlausibleEmail(email)) {
      setState(null);
      return;
    }
    let cancelled = false;
    getLoyaltyState(email, tenantId).then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, email]);

  const th = state?.threshold ?? threshold;
  const rw = state?.rewardLabel ?? reward;
  const p = state ? stampProgress(state) : null;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-2">
        <span className="text-xl" aria-hidden="true">🎁</span>
        <h3 className="text-base font-extrabold">Carte de fidélité</h3>
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {th} commande{th > 1 ? 's' : ''}{rw ? ` = ${rw}` : ' = une récompense'}
      </p>
      {p ? (
        <div className="mt-3">
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.round(p.ratio * 100)}%`, background: 'var(--brand-primary)' }}
            />
          </div>
          <p className="mt-2 text-sm font-semibold text-slate-700">
            {p.remaining === 0
              ? '🎉 Récompense débloquée !'
              : `${p.current} / ${p.total} — plus que ${p.remaining} avant votre récompense`}
          </p>
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-400">
          Renseignez votre email ci-dessus pour voir votre progression.
        </p>
      )}
    </div>
  );
}
